import { useState, useMemo } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { shallowEqual, useSelector } from 'react-redux';

import { coreSelectors, type Coupon } from '@gymcloud/core';
import config from 'web-config';
import { Modal } from 'web-components';
import { Flex, Text, H200 } from 'web-styled';

import Subscribe from './Subscribe';
import CouponForm, { type CouponState } from 'web-components/CouponForm/CouponForm';
import * as S from './styled';

const stripePromise = loadStripe(config.stripe.key);

function applyDiscount(priceStr: string, percentOff: number): { total: number; discount: number } {
  const price = parseFloat(priceStr);
  const discount = +(price * (percentOff / 100)).toFixed(2);
  const total = +(price - discount).toFixed(2);
  return { total, discount };
}

const mapState = (state) => ({
  profile: coreSelectors.getProfile(state),
});

interface Plan {
  id: number | string
  name: string
  price: string
  interval: 'year' | 'month'
}

interface Props {
  plan: Plan;
  coupon: Coupon | null;
  couponState: CouponState;
  subscriptionId: string;
  onCheckCoupon: (coupon: string) => void;
  onRequestClose: (isFinish?: boolean) => void;
}

const PriceSummary = ({ plan, discount, total }: { plan: Plan; discount: number; total: number }) => (
  <Flex column spacing={8} width="200px">
    <Text size="16px" weight="700" color="#121827">Billing Amount</Text>
    <Flex width="100%" align="center" justify="space-between">
      <Text size="16px" color="#121827">Subtotal:</Text>
      <Text size="16px" color="#121827" decoration={discount > 0 ? 'line-through' : 'inherit'}>
        ${parseFloat(plan.price).toFixed(2)}
      </Text>
    </Flex>
    <Flex width="100%" align="center" justify="space-between">
      {discount > 0 ? (
        <>
          <Text size="16px" color="#121827">Discount:</Text>
          <Text size="16px" color="#121827" weight="700">-{discount.toFixed(2)}</Text>
        </>
      ) : (
        <Text size="16px" color="#cccccc">Discount:</Text>
      )}
    </Flex>
    <Flex width="100%" align="center" justify="space-between">
      <Text size="16px" color="#121827" weight="700">Total:</Text>
      <Text size="16px" color="#121827" weight="700">${total.toFixed(2)}</Text>
    </Flex>
  </Flex>
);

const SuccessMessage = ({ email }: { email: string }) => (
  <Flex column spacing={10} padding="0 15px">
    <H200 color="primary">Thank You!</H200>
    <Text>
      Order details will be sent to <b>{email}</b>.
    </Text>
  </Flex>
);

const Checkout = ({
  plan,
  coupon,
  couponState,
  subscriptionId,
  onCheckCoupon,
  onRequestClose,
}: Props) => {
  const { profile } = useSelector(mapState, shallowEqual);
  const [isFinish, setIsFinish] = useState(false);

  const { total, discount } = useMemo(
    () => applyDiscount(plan.price, coupon?.percent_off || 0),
    [coupon, plan.price]
  )

  return (
    <Modal title="Checkout" isOpen onRequestClose={() => onRequestClose(isFinish)} contentStyle={{ width: '600px' }}>
      <S.Checkout>
        <S.Item>
          <Flex column>
            <Text color="#121827" size="16px" weight="700">Product</Text>
            <Text color="#000000" size="16px">{plan.name}</Text>
          </Flex>
          <Flex>
            <Text color="#000000" size="20px">${parseFloat(plan.price).toFixed(2)}</Text>
            <Text color="#000000" size="20px">/{plan.interval === 'year' ? 'yr' : 'mo'}</Text>
          </Flex>
        </S.Item>

        <Flex column spacing={5} width="100%" padding="15px">
          <Flex
            width="100%"
            height="160px"
            justify="space-between"
            borderBottom="1px solid #000000"
          >
            <Flex
              grow={1}
              height="100%"
              padding="0 20px 0 0"
              align="center"
              justify="center"
            >
              <CouponForm buttonText="Apply" couponState={couponState} onSubmit={onCheckCoupon} />
            </Flex>
            <Flex
              width="250px"
              height="100%"
              align="center"
              justify="center"
              borderLeft="1px solid #000000"
            >
              <PriceSummary plan={plan} discount={discount} total={total} />
            </Flex>
          </Flex>
        </Flex>

        {isFinish ? (
          <SuccessMessage email={profile.email} />
        ) : (
          <Flex column padding="15px" width="100%">
            <Elements stripe={stripePromise}>
              <Subscribe
                planId={plan.id}
                coupon={coupon?.id}
                isClient={!profile.is_pro}
                subscriptionId={subscriptionId}
                onSuccess={() => setIsFinish(true)}
              />
            </Elements>
          </Flex>
        )}
      </S.Checkout>
    </Modal>
  );
};

export default Checkout;
